import React from "react";
import { logout } from "../../store/auth.tsx";
import { fetchExamples } from "../../store/example.tsx";
import { BASE_URL } from "../../const.ts";
import {
  conversationActions,
  getWorkingConversationCount,
  MAX_CONCURRENT_QUESTIONS,
} from "../../store/conversation.tsx";
import { streamAsk } from "./streamAsk.tsx";
import handleSignUp from "./signup/handleSignUp.tsx";
import { AnonymousSession, RootState } from "../../store/types.tsx";
import { i18n } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";

interface HandleKeyPressProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSearchInputError: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchInputErrorMessage: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  onPulseAnimation?: () => void;
  stopAnimation: () => void;
  dispatch: any;
  getState: () => RootState;
  selectedConversation: string;
  selectedModel: string;
}

const getAuthenticatedKeyPressHandler = ({
  searchText,
  setSearchText,
  setSearchInputError,
  setSearchInputErrorMessage,
  onPulseAnimation,
  stopAnimation,
  dispatch,
  getState,
  selectedConversation,
  selectedModel,
}: HandleKeyPressProps) => {
  return async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();

      if (onPulseAnimation) onPulseAnimation();

      if (searchText.trim() === "") {
        setSearchInputError(true);
        stopAnimation();
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(msg({ message: "Logout", context: "Action to logout the user" }))
          .toLowerCase()
      ) {
        stopAnimation();
        dispatch(logout());
        dispatch({ type: "RESET_STATE" });
        dispatch(fetchExamples({ isLoggedIn: false }) as any);
        return;
      } else if (
        getWorkingConversationCount(getState()) >= MAX_CONCURRENT_QUESTIONS
      ) {
        stopAnimation();
        setSearchInputErrorMessage(
          i18n._(
            t`Tally is working on ${MAX_CONCURRENT_QUESTIONS} questions. Wait for one to finish.`,
          ),
        );
        return;
      } else {
        setSearchInputError(false);
        setSearchInputErrorMessage(undefined);
        setSearchText("");
      }

      let exchangeParams = {
        conversation_id: "",
        prompt: searchText,
        model: selectedModel,
      };

      if (selectedConversation !== "" && selectedConversation !== "new") {
        exchangeParams.conversation_id = selectedConversation;
      }
      await streamAsk({
        dispatch,
        getState,
        isAuthenticated: true,
        stopAnimation: stopAnimation,
        exchangeURL: "/v1/exchange/",
        streamSlug: "/v1/stream_ask/",
        exchangeParams: exchangeParams,
      });
    }
  };
};

interface UnauthenticatedHandleKeyPressProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  setSearchInputError: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchInputErrorMessage: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  onPulseAnimation?: () => void;
  stopAnimation: () => void;
  currentConversation: string;
  dispatch: any;
  getState: () => RootState;
  anonymousSession: AnonymousSession | null;
}

const getUnauthenticatedKeyPressHandler = ({
  searchText,
  setSearchText,
  setSearchInputError,
  setSearchInputErrorMessage,
  onPulseAnimation,
  stopAnimation,
  currentConversation,
  dispatch,
  getState,
  anonymousSession,
}: UnauthenticatedHandleKeyPressProps) => {
  return async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      console.log(`Anonymous session: ${anonymousSession}`);

      if (onPulseAnimation) onPulseAnimation();
      if (searchText.trim() === "") {
        setSearchInputError(true);
        stopAnimation();
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with google",
              context: "Message initiating the login flow with Google",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/auth/google-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with e-conomic",
              context: "Message initiating the login flow with e-conomic",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/v1/auth/visma-connect-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
        i18n
          ._(
            msg({
              message: "Login with Microsoft",
              context: "Message initiating the login flow with Microsoft",
            }),
          )
          .toLowerCase()
      ) {
        window.location.href = BASE_URL + "/v1/auth/microsoft-login";
        return;
      } else if (
        searchText.trim().toLowerCase() ===
          i18n
            ._(
              msg({
                message: "try for free",
                context: "Message initiating the signup flow",
              }),
            )
            .toLowerCase() ||
        (anonymousSession &&
          anonymousSession.signUpState &&
          !anonymousSession.signUpState.isComplete)
      ) {
        setSearchInputError(false);
        dispatch(
          conversationActions.appendCustomActionPrompt({
            text: searchText,
            type: "TextResponse",
          }),
        );
        await handleSignUp({
          searchText,
          dispatch,
          signUpState: anonymousSession?.signUpState,
        });
        stopAnimation();
        setSearchText("");

        return;
      } else if (
        getWorkingConversationCount(getState()) >= MAX_CONCURRENT_QUESTIONS
      ) {
        stopAnimation();
        setSearchInputErrorMessage(
          i18n._(
            t`Tally is working on ${MAX_CONCURRENT_QUESTIONS} questions. Wait for one to finish.`,
          ),
        );
        return;
      } else {
        setSearchInputError(false);
        setSearchInputErrorMessage(undefined);
        setSearchText("");
      }
      let exchangeParams = {
        conversation_id: "",
        prompt: searchText,
      };

      if (currentConversation !== "" && currentConversation !== "new") {
        exchangeParams.conversation_id = currentConversation;
      }
      await streamAsk({
        dispatch,
        getState,
        isAuthenticated: false,
        stopAnimation: stopAnimation,
        exchangeURL: "/v1/anonymous/exchange/",
        streamSlug: "/v1/anonymous/stream_ask/",
        exchangeParams: exchangeParams,
      });
    }
  };
};

export { getAuthenticatedKeyPressHandler, getUnauthenticatedKeyPressHandler };
